import { HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import { EMPTY, Observable } from 'rxjs';
import { catchError, map, mapTo, shareReplay, switchMap, tap } from 'rxjs/operators';

import { combineLatestAlwaysEmit } from '../../../helpers/observables/combine-latest-helper';
import { isNotNullOrUndefined } from '../../../helpers/typed-reactive-filters.helper';
import { CustomHttpParamEncoder } from '../../http/entities/custom-http-param-encoder';
import { Paginated } from '../../http/entities/paginated.entity';
import { ShAbstractHttpService } from '../../http/services/http.abstract.service';
import { ShPaginationService } from '../../http/services/pagination.service';
import { OrganisationId } from '../../organisations/entities/organisation.entity';
import { RecordType } from '../../records/entities/metadata/metadata.entity';
import { User } from '../../users/entities/user.entity';
import { UserService } from '../../users/services/user.service';
import { ExtendedGenericListProfileField } from '../entities/extended-profile-field.entity';
import { ExtendedFacetProfile } from '../entities/extended-profile.entity';
import { ProfileField } from '../entities/profile-field.entity';
import { ExportProfile, FacetProfile, Profile, ProfileCategory } from '../entities/profile.entity';
import { ProfileHelper } from '../helpers/profile.helper';

export enum FacetProfileTag {
    ALL_OBJECTS = 'Mh2.All.Facet',
    CONCEPTS = 'Mh2.Concept.Facet',
    PUBLISHED = 'Mh2.Published.Facet',
    CONTENT = 'Mh2.Content.Facet',
    TASKS = 'Mh2.Tasks.Facet',
    PORTAL = 'Mh2.Portal.Facet',
    ARCHIVED = 'Mh2.Archived.Facet',
    RECYLE = 'Mh2.Recycle.Facet',
    SELECTION = 'Mh2.Selection.Facet',
    REPRESENTATIONS = 'Mh2.Representations.Facet'
}
export enum ListProfileTag {
    ALL_OBJECTS = 'Mh2.All.Column',
    CONCEPTS = 'Mh2.Concept.Column',
    PUBLISHED = 'Mh2.Published.Column',
    CONTENT = 'Mh2.Content.Column',
    TASKS = 'Mh2.Tasks.Column',
    PORTAL = 'Mh2.Portal.Column',
    ARCHIVED = 'Mh2.Archived.Column',
    RECYLE = 'Mh2.Recycle.Column',
    SELECTION = 'Mh2.Selection.Column',
    REPRESENTATIONS = 'Mh2.Representations.Column'
}

@Injectable({ providedIn: 'root' })
export class ProfileDataService {
    protected readonly FALLBACK_VALUE: Profile[] = [];
    protected readonly TYPE_NAME: string = 'Profile';

    protected readonly PROFILES_ENDPOINT: string = `v2/profiles`;
    private readonly DEFAULT_ADVANCED_SEARCH_TAG: string = 'Mh2.Search';
    private readonly DEFAULT_FACET_TAG: string = 'Mh2.Facet';
    private readonly DEFAULT_LIST_TAG: string = 'Mh2.Column';
    private readonly DEFAULT_AI_TAG: string = 'Default.Ai';
    protected exportProfileCount$: Observable<number>;

    // Injectors
    private readonly pagination: ShPaginationService = inject(ShPaginationService);
    private readonly httpService: ShAbstractHttpService = inject(ShAbstractHttpService);
    private readonly profileHelper: ProfileHelper = inject(ProfileHelper);
    private readonly userService: UserService = inject(UserService);

    constructor() {
        this.initialize();
    }

    // #region GET
    private initialize() {
        this.initExportProfileCount();
    }

    protected initExportProfileCount(): void {
        this.exportProfileCount$ = this.userService.getCurrentUser().pipe(
            map((user: User) => user.Organisation.Id),
            switchMap(orgId => this.countProfilesOfType(orgId, ProfileCategory.Export)),
            shareReplay(1)
        );
    }

    protected fetchProfilesOfCategory(
        category: ProfileCategory,
        orgId?: OrganisationId,
        tag?: string
    ): Observable<Profile[]> {
        let params: HttpParams = new HttpParams({ encoder: new CustomHttpParamEncoder() }).set('Category', category);
        if (orgId) {
            params = params.set('organisationId', orgId);
        }

        if (tag) {
            params = params.set('tag', tag);
        }

        return this.pagination.getAll<Profile>(this.PROFILES_ENDPOINT, params);
    }

    private fetchSingleProfile(profileId: string): Observable<Profile> {
        const url: string = `${this.PROFILES_ENDPOINT}/${profileId}`;

        return this.httpService.get(url);
    }

    private countProfilesOfType(orgId: OrganisationId, category: ProfileCategory): Observable<number> {
        const params: HttpParams = new HttpParams({ encoder: new CustomHttpParamEncoder() })
            .set('organisationId', orgId)
            .set('Category', category)
            .set('nrOfResults', 0);

        return this.httpService
            .get<Paginated<Profile>>(this.PROFILES_ENDPOINT, params)
            .pipe(map(response => response.TotalNrOfResults));
    }

    // #endregion

    // #region Getters

    // Only used by management
    public getAllFacetProfiles(organisationId?: OrganisationId): Observable<FacetProfile[]> {
        return this.fetchProfilesOfCategory(ProfileCategory.Facet, organisationId) as Observable<FacetProfile[]>;
    }

    public getAllExportProfiles(organisationId?: OrganisationId): Observable<ExportProfile[]> {
        if (organisationId) {
            return this.fetchProfilesOfCategory(ProfileCategory.Export, organisationId) as Observable<ExportProfile[]>;
        }
        return this.userService.getCurrentUser().pipe(
            map((user: User) => user.Organisation.Id),
            switchMap(
                orgId => this.fetchProfilesOfCategory(ProfileCategory.Export, orgId) as Observable<ExportProfile[]>
            ),
            shareReplay(1)
        );
    }

    public getNumberOfExportProfiles(): Observable<number> {
        return this.exportProfileCount$;
    }

    public getAllAdvancedSearchProfiles(organisationId?: OrganisationId): Observable<Profile[]> {
        return this.fetchProfilesOfCategory(ProfileCategory.AdvancedSearch, organisationId);
    }

    public getAllGenericListProfiles(organisationId?: OrganisationId): Observable<Profile[]> {
        return this.fetchProfilesOfCategory(ProfileCategory.GenericList, organisationId);
    }

    public get(
        profileId: string,
        category?: ProfileCategory,
        organisationId?: OrganisationId
    ): Observable<Profile | undefined>;

    public get(
        profileIds: string[],
        category?: ProfileCategory,
        organisationId?: OrganisationId
    ): Observable<Profile[]>;

    public get(
        profileId: string | string[],
        category?: ProfileCategory,
        organisationId?: OrganisationId
    ): Observable<(Profile | undefined) | Profile[]> {
        if (Array.isArray(profileId)) {
            return this.getMultiple(profileId, category);
        } else {
            return this.getSingle(profileId, category, organisationId);
        }
    }

    // TODO: Move to profile helper
    public filterProfilesIdsForRecordType(profileIds: string[], recordType: RecordType): Observable<string[]> {
        return this.getMultiple(profileIds).pipe(
            map(profiles =>
                profiles
                    .filter(profile => profile.RecordTypes.length === 0 || profile.RecordTypes.includes(recordType))
                    .map(profile => profile.Id)
            )
        );
    }

    private getMultiple(
        profileIds: string[],
        category?: ProfileCategory,
        organisationId?: OrganisationId
    ): Observable<Profile[]> {
        return combineLatestAlwaysEmit(
            profileIds.map(profileId => this.getSingle(profileId, category, organisationId))
        ).pipe(map(profiles => profiles.filter((profile): profile is Profile => typeof profile !== 'undefined')));
    }

    public getSingle(
        profileId: string,
        category: ProfileCategory = ProfileCategory.Base,
        organisationId?: OrganisationId
    ): Observable<Profile | undefined> {
        let profiles$: Observable<Profile[]>;
        switch (category) {
            case ProfileCategory.AdvancedSearch:
                profiles$ = this.getAllAdvancedSearchProfiles(organisationId);
                break;
            case ProfileCategory.Facet:
                profiles$ = this.getAllFacetProfiles(organisationId);
                break;
            case ProfileCategory.GenericList:
                profiles$ = this.getAllGenericListProfiles(organisationId);
                break;
            default:
                return this.fetchSingleProfile(profileId);
        }

        return profiles$.pipe(map(profiles => profiles.find(profile => profile.Id === profileId)));
    }

    public getAdvancedSearchProfiles(
        organisationId: OrganisationId,
        tag: string = this.DEFAULT_ADVANCED_SEARCH_TAG
    ): Observable<Profile[]> {
        return this.getAllAdvancedSearchProfiles(organisationId).pipe(
            map(profiles => profiles.filter(profile => profile.Tag?.startsWith(tag)))
        );
    }

    public getAdvancedSearchProfileFields(
        organisationId: OrganisationId,
        tag: string = this.DEFAULT_ADVANCED_SEARCH_TAG
    ): Observable<ProfileField[]> {
        return this.getAdvancedSearchProfiles(organisationId, tag).pipe(
            map(profiles => profiles.flatMap(profile => profile.Fields)),
            isNotNullOrUndefined()
        );
    }

    public getFacetProfiles(
        organisationId: OrganisationId,
        tag: string = this.DEFAULT_FACET_TAG
    ): Observable<ExtendedFacetProfile[]> {
        return this.getAllFacetProfiles(organisationId).pipe(
            map(profiles => profiles.filter(profile => profile.Tag?.startsWith(tag))),
            tap(profiles => {
                if (profiles.length === 0) {
                    console.error(`ProfileDataService:: No facet profile found.`);
                }
            }),
            switchMap(profiles => this.profileHelper.extendProfiles(profiles) as Observable<ExtendedFacetProfile[]>)
        );
    }

    public getAiProfile(organisationId: OrganisationId, tag: string = this.DEFAULT_AI_TAG): Observable<Profile> {
        return this.fetchProfilesOfCategory(ProfileCategory.Ai, organisationId, tag).pipe(
            tap(profiles => {
                if (profiles.length === 0) {
                    console.error(`ProfileDataService:: No AI profile found.`);
                } else if (profiles.length > 1) {
                    console.warn(`ProfileDataService:: More than one AI profile found. Using the first one.`);
                }
            }),
            map(profiles => profiles[0])
        );
    }

    public getListProfileFields(
        organisationId: OrganisationId,
        tag: string = this.DEFAULT_LIST_TAG
    ): Observable<ExtendedGenericListProfileField[]> {
        return this.getAllGenericListProfiles(organisationId).pipe(
            map(profiles => profiles.filter(profile => profile.Tag?.startsWith(tag))),
            map(profiles => {
                if (profiles.length === 0) {
                    console.error(`ProfileDataService:: No list profile found with tag ${tag}.`);
                    return [];
                }
                return profiles;
            }),
            switchMap(profiles => this.profileHelper.extendProfiles(profiles)),
            map(profiles => profiles.flatMap((profile: Profile) => profile.Fields) as ExtendedGenericListProfileField[])
        );
    }
    // #endregion

    // #region Save
    public saveProfile(profile: Profile): Observable<boolean> {
        const profileEndpoint: string = `${this.PROFILES_ENDPOINT}/${profile.Id}`;

        return this.httpService.put<Profile>(profileEndpoint, profile).pipe(mapTo(true));
    }

    public updateProfileField(profileId: string, profileField: Partial<ProfileField>): Observable<boolean> {
        const url: string = `${this.PROFILES_ENDPOINT}/${profileId}/fields/${profileField.DottedKey}`;

        return this.httpService.put(url, this.cleanProfileField(profileField));
    }

    // #endregion

    // #region Create
    public createProfile(profile: Profile): Observable<Profile> {
        return this.httpService.post<Profile>(this.PROFILES_ENDPOINT, profile).pipe(
            catchError(error => {
                console.error(`ProfileDataService:: Error while saving profile.`, error);
                return EMPTY;
            })
        );
    }

    public createProfileField(profileId: string, profileField: Partial<ProfileField>): Observable<boolean> {
        const url: string = `${this.PROFILES_ENDPOINT}/${profileId}/fields/`;
        return this.httpService.post(url, this.cleanProfileField(profileField));
    }
    // #endregion

    // #region Delete
    public deleteProfile(profileId: string): Observable<boolean> {
        const url: string = `${this.PROFILES_ENDPOINT}/${profileId}`;
        return this.httpService.delete(url).pipe(mapTo(true));
    }

    public deleteProfileField(profileId: string, fieldId: string): Observable<boolean> {
        const url: string = `${this.PROFILES_ENDPOINT}/${profileId}/fields/${fieldId}`;
        return this.httpService.delete(url).pipe(mapTo(true));
    }
    // #endregion

    // #region Utils
    private cleanProfileField(profileField: Partial<ProfileField>): Partial<ProfileField> {
        const isTopLevelField: boolean = profileField.DottedKey?.split('.').length === 2;

        // MED-4590: Scope is only applicable for top level fields
        if (!isTopLevelField) {
            delete profileField.Scope;
        }

        return profileField;
    }

    // #endregion
}
